import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plane, UserX, X } from 'lucide-react';
import { groupPeople, type AwayCounts, type AwayInput, type AwayPeriod, type MyPlayer, type MyScheduleAvailability, type PlayerAvailability } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../../lib/api';
import { formatLongDay, today } from '../../lib/dates';
import { Alert, Button, Dialog, Field } from '../ui';

// ---------- Data ----------

export function useMyPlayers() {
  return useQuery({ queryKey: ['me', 'players'], queryFn: () => api.get<MyPlayer[]>('/me/players'), staleTime: 60_000 });
}

/** Your players' answers for the sessions and games in a range (players and parents). */
export function useMyAvailability(from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: ['me-availability', from, to],
    queryFn: () => api.get<MyScheduleAvailability>(`/me/availability?from=${from}&to=${to}`),
    enabled,
    placeholderData: (prev) => prev,
  });
}

/** Who's out of each session and game in a range (coaches). */
export function useAwayCounts(from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: ['away-counts', from, to],
    queryFn: () => api.get<AwayCounts>(`/me/away-counts?from=${from}&to=${to}`),
    enabled,
    placeholderData: (prev) => prev,
  });
}

function useRefresh() {
  const qc = useQueryClient();
  return () => {
    for (const k of ['me-availability', 'away-counts', 'availability', 'away']) void qc.invalidateQueries({ queryKey: [k] });
  };
}

const short = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-AU', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });

// ---------- On a session or game ----------

/** "Mia can't make it" / "Can't make it" under a session or game in the schedule. */
export function AnswerRow({ kind, teamId, itemId, title, players, compact }: { kind: 'session' | 'game'; teamId: string; itemId: string; title: string; players: PlayerAvailability[]; compact?: boolean }) {
  const refresh = useRefresh();
  const [asking, setAsking] = useState<PlayerAvailability | null>(null);
  const [reason, setReason] = useState('');
  const path = `/teams/${teamId}/${kind === 'session' ? 'sessions' : 'games'}/${itemId}/availability`;
  const set = useMutation({
    mutationFn: (b: { playerId: string; unavailable: boolean; reason?: string }) => api.put(path, b),
    onSuccess: () => {
      setAsking(null);
      setReason('');
      refresh();
    },
  });
  const text = compact ? 'text-[11px]' : 'text-[13px]';
  return (
    <div className={`space-y-1 border-t border-line ${compact ? 'mt-1 pt-1' : 'mt-2 pt-2'}`}>
      {players.map((p) => (
        <div key={p.playerId} className={`flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 ${text}`}>
          <span className={`min-w-0 ${p.unavailable ? 'text-warn' : 'text-muted'}`}>
            {players.length > 1 || p.unavailable ? <span className="font-medium">{p.name.split(' ')[0]} </span> : null}
            {p.unavailable ? (p.away ? 'away' : 'can’t make it') : players.length > 1 ? 'coming' : ''}
          </span>
          {p.unavailable ? (
            <button className="font-medium text-action hover:underline" disabled={set.isPending} onClick={() => set.mutate({ playerId: p.playerId, unavailable: false })}>
              {compact ? 'Coming' : 'Coming after all'}
            </button>
          ) : (
            <button className="inline-flex items-center gap-1 font-medium text-action hover:underline" onClick={() => setAsking(p)}>
              <UserX className={compact ? 'size-3' : 'size-3.5'} aria-hidden /> Can’t make it
            </button>
          )}
        </div>
      ))}
      {set.isError && !asking && <p className={`${text} text-danger`}>{errorMessage(set.error)}</p>}
      <Dialog open={!!asking} onClose={() => { setAsking(null); set.reset(); }} title={`${asking?.name.split(' ')[0] ?? ''} can’t make ${kind === 'game' ? 'the game' : title}?`}>
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (asking) set.mutate({ playerId: asking.playerId, unavailable: true, reason: reason.trim() || undefined });
          }}
          className="space-y-4"
        >
          <Field label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="e.g. dentist" hint="The coaches see this." autoFocus />
          {set.isError && <Alert>{errorMessage(set.error)}</Alert>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setAsking(null)}>
              Cancel
            </Button>
            <Button type="submit" loading={set.isPending}>
              Tell the coach
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}

/** For coaches: "2 away" on a session or game, names on hover. */
export function OutChip({ list, compact }: { list?: { name: string; reason: string | null }[]; compact?: boolean }) {
  if (!list?.length) return null;
  const title = list.map((o) => `${o.name}${o.reason ? `: ${o.reason}` : ''}`).join('\n');
  return (
    <span className={`inline-flex items-center gap-1 rounded-full bg-warn-soft px-1.5 font-medium text-warn ${compact ? 'text-[11px]' : 'text-[12px]'}`} title={title}>
      <UserX className="size-3" aria-hidden />
      {list.length} {compact ? 'out' : 'can’t make it'}
    </span>
  );
}

// ---------- Away for a stretch ----------

export function AwayButton({ players }: { players: MyPlayer[] }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        <Plane className="size-4" aria-hidden /> I’m away
      </Button>
      {open && <AwayDialog players={players} onClose={() => setOpen(false)} />}
    </>
  );
}

function AwayDialog({ players, onClose }: { players: MyPlayer[]; onClose: () => void }) {
  const refresh = useRefresh();
  const people = groupPeople(players);
  const [chosen, setChosen] = useState<string[]>(people.length === 1 ? [people[0]!.key] : []);
  const [startDate, setStart] = useState(today());
  const [endDate, setEnd] = useState(today());
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api.post<AwayPeriod[]>('/away', { playerIds: people.filter((p) => chosen.includes(p.key)).flatMap((p) => p.playerIds), startDate, endDate, reason: reason.trim() || null } satisfies AwayInput),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  const f = fieldErrors(save.error);
  const self = players.every((p) => p.kind === 'player');
  return (
    <Dialog open onClose={onClose} title={self ? 'Away for a while?' : 'Who’s away?'}>
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="space-y-4" noValidate>
        <p className="text-[14px] text-muted">Every session and game in these dates is marked “can’t make it”, on every team, including ones the coach adds later. You can still say “coming after all” for any one of them.</p>
        {people.length > 1 && (
          <fieldset className="space-y-1">
            <legend className="mb-1 text-[14px] font-medium">Who</legend>
            {people.map((p) => (
              <label key={p.key} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-sunken">
                <input type="checkbox" className="size-4 accent-[var(--action)]" checked={chosen.includes(p.key)} onChange={(e) => setChosen(e.target.checked ? [...chosen, p.key] : chosen.filter((x) => x !== p.key))} />
                <span>
                  {p.name} <span className="text-[13px] text-muted">({p.teamNames.join(', ')})</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="First day" type="date" value={startDate} onChange={(e) => { setStart(e.target.value); if (e.target.value > endDate) setEnd(e.target.value); }} error={f.startDate} />
          <Field label="Last day" type="date" value={endDate} min={startDate} onChange={(e) => setEnd(e.target.value)} error={f.endDate} />
        </div>
        <Field label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="e.g. family holiday" hint="The coaches see this." />
        {save.isError && !f.startDate && !f.endDate && <Alert>{errorMessage(save.error)}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending} disabled={!chosen.length}>
            Tell the coaches
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Upcoming away times you've set, with Cancel. */
export function AwayList() {
  const refresh = useRefresh();
  const q = useQuery({ queryKey: ['away'], queryFn: () => api.get<AwayPeriod[]>('/away') });
  const remove = useMutation({ mutationFn: (groupId: string) => api.del<AwayPeriod[]>(`/away/${groupId}`), onSuccess: refresh });
  if (!q.data?.length) return null;
  return (
    <ul className="mb-5 space-y-1.5">
      {q.data.map((a) => (
        <li key={a.groupId} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-warn/40 bg-warn-soft px-3 py-2 text-[14px]">
          <Plane className="size-4 shrink-0 text-warn" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="font-medium">{a.name}</span> away {a.startDate === a.endDate ? formatLongDay(a.startDate) : `${short(a.startDate)} to ${short(a.endDate)}`}
            {a.reason ? ` (${a.reason})` : ''}
            <span className="text-muted"> · {a.teamNames.join(', ')}</span>
          </span>
          <button className="inline-flex items-center gap-1 text-[13px] font-medium text-action hover:underline" onClick={() => remove.mutate(a.groupId)} disabled={remove.isPending}>
            <X className="size-3.5" aria-hidden /> Cancel
          </button>
        </li>
      ))}
      {remove.isError && <li><Alert>{errorMessage(remove.error)}</Alert></li>}
    </ul>
  );
}
